import { ButtonProps, HStack, Skeleton, Text, useColorModeValue, VStack } from "@chakra-ui/react"
import Big from "big.js"
import { LongShortNoCircle } from "component/Icon"
import { MotionBox } from "component/Motion"
import { TxButton } from "component/TxButton"
import { EventName } from "module/analytics/types"
import { SlippagePopover } from "module/pool/component/LiquidityForms/SlippagePopover"
import { Web3WalletContainer } from "module/wallet/container/Web3WalletContainer"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { RiFlashlightFill } from "react-icons/ri"
import { PositionOpen } from "sdk-react/clearingHouse/usePositionOpen"
import { DelegateApproval } from "sdk-react/limitOrder/useDelegateApproval"

// TODO: review this component carefully

// Button to approve OTC contract
interface ApproveOtcOrderButtonProps extends ButtonProps {}
export function ApproveOtcOrderButton({ ...buttonProps }: ApproveOtcOrderButtonProps) {
    const { approve, isApproved } = DelegateApproval.useContainer()

    const handleClick = useCallback(() => {
        if (!isApproved) {
            return approve()
        } else {
            // revoke()
        }
    }, [approve, isApproved])

    return (
        <TxButton
            colorScheme="perpetual"
            variant="solid"
            size="sm"
            onClick={handleClick}
            trackingProps={{ eventName: EventName.OTC_ORDER_APPROVE_CLICK }}
            {...buttonProps}
        >
            Approve Smart Maker
        </TxButton>
    )
}

// Button to submit order, shows a flash icon if using OTC entry price
interface TradingSubmitButtonProps extends ButtonProps {
    isShort: boolean
    marketOrderDisabled?: boolean
    otcOrderDisabled?: boolean
}

export function TradingSubmitButton({
    isShort,

    marketOrderDisabled,
    otcOrderDisabled,

    isDisabled,
    ...buttonProps
}: TradingSubmitButtonProps) {
    const { t } = useTranslation()
    const positionSideStr = isShort ? t("position.side_short") : t("position.side_long")
    const subText = useColorModeValue("gray.500", "gray.400")
    const { isChecking, isApproved } = DelegateApproval.useContainer()
    const { account } = Web3WalletContainer.useContainer()

    const { isUsingOtcEntryPrice, otcEntryPrice, coreEntryPrice } = PositionOpen.useContainer()

    function calculatePercentageDifference(
        otcEntryPrice: Big | undefined,
        coreEntryPrice: Big | undefined,
    ): string | undefined {
        if (otcEntryPrice && coreEntryPrice) {
            return otcEntryPrice.minus(coreEntryPrice).div(coreEntryPrice).times(100).toFixed(4) + "%"
        }
        return undefined
    }

    const percentageDifference = calculatePercentageDifference(otcEntryPrice, coreEntryPrice)

    // if otc price is better, check approval status
    if (isUsingOtcEntryPrice) {
        return (
            <>
                {!account || isApproved ? (
                    <HStack w={"full"}>
                        <VStack w={"full"} gap={1}>
                            {percentageDifference && isUsingOtcEntryPrice && (
                                <HStack>
                                    <RiFlashlightFill size={20} style={{ flexShrink: 0 }} />
                                    <Text fontSize="xs" color={subText}>
                                        Better price found ({percentageDifference}) via{" "}
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href="https://blog.perp.fi/introducing-smart-maker-b9bb9324ebf"
                                        >
                                            Smart Maker
                                        </a>
                                        .
                                    </Text>
                                </HStack>
                            )}
                            <TxButton
                                colorScheme={isShort ? "red" : "green"}
                                trackingProps={{ eventName: EventName.OTC_ORDER_SUBMIT_CLICK }}
                                leftIcon={
                                    <MotionBox boxSize="20px">
                                        <MotionBox
                                            boxSize="20px"
                                            initial={{ rotate: isShort ? 90 : 0 }}
                                            animate={{
                                                rotate: isShort ? 90 : 0,
                                            }}
                                        >
                                            <LongShortNoCircle boxSize="20px" />
                                        </MotionBox>
                                    </MotionBox>
                                }
                                isDisabled={otcOrderDisabled}
                                {...buttonProps}
                            >
                                {t("position.confirm_open_position", { positionSide: positionSideStr })}
                            </TxButton>
                        </VStack>
                        <SlippagePopover />
                    </HStack>
                ) : (
                    <Skeleton borderRadius={"lg"} isLoaded={!isChecking}>
                        <VStack alignItems={"stretch"}>
                            {!isDisabled && percentageDifference && isUsingOtcEntryPrice && (
                                <HStack>
                                    <RiFlashlightFill size={20} style={{ flexShrink: 0 }} />
                                    <Text fontSize="xs" color={subText}>
                                        Better price found ({percentageDifference}) via{" "}
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href="https://blog.perp.fi/introducing-smart-maker-b9bb9324ebf"
                                        >
                                            Smart Maker
                                        </a>
                                        . Approve to continue.
                                    </Text>
                                </HStack>
                            )}
                            <ApproveOtcOrderButton />
                        </VStack>
                    </Skeleton>
                )}
            </>
        )
    }
    // if not using otc price, just submit order
    return (
        <HStack w={"full"}>
            <TxButton
                colorScheme={isShort ? "red" : "green"}
                leftIcon={
                    <MotionBox boxSize="20px">
                        <MotionBox
                            boxSize="20px"
                            initial={{ rotate: isShort ? 90 : 0 }}
                            animate={{
                                rotate: isShort ? 90 : 0,
                            }}
                        >
                            <LongShortNoCircle boxSize="20px" />
                        </MotionBox>
                    </MotionBox>
                }
                isDisabled={marketOrderDisabled}
                {...buttonProps}
            >
                {t("position.confirm_open_position", { positionSide: positionSideStr })}
            </TxButton>
            <SlippagePopover />
        </HStack>
    )
}
